import { ICartLineItem } from "redux/reducers/models/cart";
import { IUserPermissions } from "redux/reducers/models/user";

type RemoveLineItemPermissions =
  | Pick<IUserPermissions, "orders_remove_line_item" | "orders_remove_line_item_after_send">
  | null
  | undefined;

type LineItemForPermissions = Pick<
  ICartLineItem,
  "quantity_sent" | "kitchen_chit_id" | "kitchen_status" | "quantity"
> | null | undefined;

function cartLineItemQuantitySent(lineItem: LineItemForPermissions): number {
  const quantitySent = lineItem?.quantity_sent ?? 0;
  if (quantitySent > 0) {
    return quantitySent;
  }

  if (lineItem?.kitchen_chit_id || lineItem?.kitchen_status === "sent") {
    return lineItem?.quantity ?? 0;
  }

  return 0;
}

function isLineItemSentToKitchen(lineItem: LineItemForPermissions): boolean {
  return cartLineItemQuantitySent(lineItem) > 0;
}

/** Whether the user may fully remove a line item from the cart. */
export function canRemoveCartLineItem(
  permissions: RemoveLineItemPermissions,
  lineItem?: LineItemForPermissions,
): boolean {
  if (!permissions?.orders_remove_line_item) {
    return false;
  }

  if (isLineItemSentToKitchen(lineItem)) {
    return !!permissions?.orders_remove_line_item_after_send;
  }

  return true;
}

/** Lowest quantity allowed in the quantity field. */
export function minCartLineItemQuantity(
  permissions: RemoveLineItemPermissions,
  lineItem?: LineItemForPermissions,
): number {
  if (canRemoveCartLineItem(permissions, lineItem)) {
    return 0;
  }

  const quantitySent = cartLineItemQuantitySent(lineItem);
  if (quantitySent > 0) {
    return quantitySent;
  }

  return 1;
}

/** Whether the user may set a line item to this quantity. */
export function canSetCartLineItemQuantity(
  permissions: RemoveLineItemPermissions,
  newQuantity: number,
  lineItem?: LineItemForPermissions,
): boolean {
  if (!Number.isFinite(newQuantity)) {
    return false;
  }

  if (newQuantity === 0) {
    return canRemoveCartLineItem(permissions, lineItem);
  }

  return true;
}
